<template>
  <!--
      The pdfviewer creates an overflow on the page while it's loading
      When we know it is in a workable state, we can finally remove the overflow of the page
      We cannot do this before it loads because the pdfviewer will not be able to render at all otherwise
  -->
  <div
    class="fill-height"
    :class="{ 'overflow-hidden': viewerInitialized }"
    data-locator="document-viewer"
  >
    <div class="my-2">
      <v-row class="document-viewer-title-bar-row bg-white px-2 px-sm-6">
        <v-col cols="12">
          <DocumentViewerHeader
            v-if="!isLoading"
            :file="file"
            :mnemo="mnemo"
            :isRedactMode="redactPaneOpen"
            :isRedactButtonDisabled="isRedactButtonDisabled"
            @close="toggleRedactPane"
          />
          <v-skeleton-loader v-else width="400" type="text" style="background-color: inherit" />
        </v-col>
      </v-row>
    </div>

    <DocumentViewerErrors
      v-if="fileFromPdfjsError || fileError"
      :fileFromPdfjsError="fileFromPdfjsError"
      :errorCode="errorCode"
      :fileName="file.name"
    />
    <template v-else>
      <div v-if="!viewerLoaded" class="fill-height d-flex align-center flex-column justify-center">
        <AppLoader/>
        <p class="mt-2">{{ $t('common.DocumentViewer.loadingDocumentLabel') }}</p>
      </div>

      <DocumentPdfViewer
        ref="documentPdfViewer"
        :mnemo="mnemo"
        :fileId="fileId"
        @pages-loaded="onViewerPagesLoaded"
        @scale-changing="zoomLevel = $event"
        @initialized="initializeViewer"
        @load-file-from-pdfjs-error="handleloadFileFromPdfjsError"
      >
        <template #left-side-append>
          <!-- NOTE: If other tools aside from redacting are added, we should make a more flexible condition system -->
          <v-divider
            v-if="shouldShowRedactAction"
            vertical
            class="my-auto"
            style="border-width: 1px; height: 16px;"
          />
          <app-button
            v-if="shouldShowRedactAction"
            type="ghost"
            variant="neutral"
            size="small"
            class="ml-2"
            data-locator="redact-button"
            :class="{ '--is-active': redactPaneOpen }"
            @click="toggleRedactPane()"
          >
            <app-icon-ds
              icon="highlighter-line"
              size="xs"
            />
            {{ $t('common.DocumentViewer.redactLabel') }}
          </app-button>
        </template>

        <template v-if="redactDocumentError || pollingRedactDocumentError" #toolbar-append>
          <div :class="{ 'toolbar-expanded': redactPaneOpen }">
            <app-alert-in-page
              :hasLeftBorder="false"
              :rounded="false"
              icon-name="diamond-exclamation"
              variant="error"
              style="margin-top: 7px"
            >
              {{ $t('project.documents.DocumentViewer.redactDocumentErrorAlert') }}
            </app-alert-in-page>
          </div>
        </template>
      </DocumentPdfViewer>

      <PdfRedactingSidebar
        v-model:isRedactByZone="isRedactByZone"
        v-model:isRedactByAI="isRedactByAI"
        ref="redactSidebar"
        class="right-sidebar-container"
        :pdfAppInstance="$refs.documentPdfViewer ? $refs.documentPdfViewer.getPdfAppInstance() : null"
        :fileId="fileId"
        :mnemo="mnemo"
        :isPending="isLoading || !viewerLoaded"
        :open="redactPaneOpen"
        :drawnRectangles="drawnRectangles"
        :activeRectangleId="activeRectangleId"
        :displayOcrMessage="!file.pdfHasText && !isOcrConversionPending"
        :pdfHasText="!!file.pdfHasText"
        @set-active-rectangle-id="scrollAndFocusOnRectangle"
        @duplicate-on-all-pages="onDuplicateRectangle"
        @delete-on-all-pages="onDeleteRectangleOnAllPages"
        @delete-rectangle="onDeleteRectangle"
        @go-back="preventSidebarGoBack = $event"
        @close="toggleRedactPane"
        @update:isRedactByKeywords="isRedactByKeywords = $event"
        @updateRectangles="onUpdateSearchedRectangles"
        @fully-toggled="onRedactPaneFullyToggled"
        @launch-ocr-conversion="launchOcrConversion"
      >
      </PdfRedactingSidebar>

      <DocumentPdfViewerRectangleDrawer
        v-if="showRectangleDrawer"
        ref="rectangleDrawer"
        :pdfAppInstance="$refs.documentPdfViewer.getPdfAppInstance()"
        :zoomLevel="zoomLevel"
        :isRedactByZone="isRedactByZone"
        :isRedactByKeywords="isRedactByKeywords"
        :isRedactByAI="isRedactByAI"
        :searchedRectangles="searchedRectangles"
        :activeRectangleId="activeRectangleId"
        @set-active-rectangle-id="scrollAndFocusOnRectangle"
        @updateDrawnRectangles="drawnRectangles = $event"
      />

      <!-- DIALOGS -->
      <RedactDocumentDialog
        v-if="redactDocumentDialogIsOpen"
        :mnemo="mnemo"
        :fileId="fileId"
        :redactedZones="allRectangles"
        :isRedactByZone="isRedactByZone"
        :pdfAppInstance="$refs.documentPdfViewer.getPdfAppInstance()"
        :preventRedactPaneClose="preventRedactPaneClose"
        @quit-without-saving="quitWithoutSaving"
        @confirm="confirmRedactDocument"
        @polling-error="pollingRedactDocumentError = $event"
        @close="closeRedactDocumentDialog"
      />
      <DocumentsNotRedactableDialog
        v-if="showDocumentNotRedactableDialog"
        :reasons="filteredRedactionBlockedReasons"
        @close="documentNotRedactableDialogIsOpen = false"
      />
      <RedactStorageWarningDialog
        v-if="redactStorageWarningDialogIsOpen"
        @cancel="redactStorageWarningDialogIsOpen = false"
        @confirm="onRedactStorageWarningConfirm"
      />
    </template>

    <DocumentViewerOcrProgress
      v-if="isOcrConversionPending"
      :progress=ocrConversionProgress
    />
  </div>
</template>

<script>
  import { mapState, mapActions, mapMutations, mapGetters } from 'vuex'

  import AppLoader from '@/common/AppLoader.vue'
  import { BROADCAST } from '@/common/constants'
  import DocumentPdfViewer from '@/common/document-viewer/DocumentPdfViewer.vue'
  import DocumentPdfViewerRectangleDrawer from '@/common/document-viewer/DocumentPdfViewerRectangleDrawer.vue'
  import DocumentViewerErrors from '@/common/document-viewer/DocumentViewerErrors.vue'
  import DocumentViewerHeader from '@/common/document-viewer/DocumentViewerHeader.vue'
  import DocumentViewerOcrProgress from '@/common/document-viewer/DocumentViewerOcrProgress.vue'
  import RedactDocumentDialog from '@/common/document-viewer/RedactDocumentDialog.vue'
  import PdfRedactingSidebar from "@/common/document-viewer/side-bar/PdfRedactingSidebar.vue"
  import { getFileExtension } from '@/common/utils/files'
  import { OcrNotifications } from '@/common/utils/notifications'
  import { toast } from '@/common/utils/toast'
  import { getUserSetting, setUserSetting } from '@/common/utils/userSettings'
  import DocumentsNotRedactableDialog from '@/project/documents/dialogs/DocumentsNotRedactableDialog.vue'
  import RedactStorageWarningDialog from '@/project/documents/dialogs/RedactStorageWarningDialog.vue'
  import { ROOM_DOCUMENT_VIEWER_ROUTE_NAME, ROOM_ROUTE_TYPES } from '@/router'
  import documentsService from '@/services/documents.service'
  import { realtimeService } from '@/services/realtime.service'
  import roomService from '@/services/room.service'
  import { DocumentViewerMutationTypes } from '@/store/modules/document-viewer/mutation_types'
  import { FilesActionTypes } from '@/store/modules/files/action_types'

  import { track } from '../pendo/agent'
  import { getRoomRouteType } from '../utils/rooms'

  const REDACT_WARNING_KEY = 'hide-redact-storage-warning'

  const IGNORED_REDACTION_BLOCKED_REASONS = [
    'NOT_PM',
    'NOT_DATAROOM',
    'MISSING_SUBSCRIPTION_OPTION',
  ]

  export default {
    name: 'DocumentViewer',
    components: {
      DocumentViewerOcrProgress,
      PdfRedactingSidebar,
      DocumentViewerErrors,
      DocumentViewerHeader,
      AppLoader,
      DocumentPdfViewer,
      RedactDocumentDialog,
      DocumentsNotRedactableDialog,
      DocumentPdfViewerRectangleDrawer,
      RedactStorageWarningDialog,
    },
    props: {
      mnemo: {
        type: String,
        required: true,
      },
      fileId: {
        type: [String, Number],
        required: true,
      },
      roomType: {
        type: String,
        required: true,
      },
    },
    head () {
      return {
        title: this.pageTitle,
      }
    },
    data () {
      return {
        isLoading: true,
        viewerInitialized: false,
        viewerLoaded: false,
        file: {},
        filePromise: null,
        zoomLevel: 1,
        redactPaneOpen: false,
        showRectangleDrawer: false,
        isRedactByZone: false,
        isRedactByAI: false,
        isRedactByKeywords: false,
        drawnRectangles: [],
        searchedRectangles: [],
        preventRedactPaneClose: false,
        preventSidebarGoBack: false,
        pollingRedactDocumentError: null,
        fileFromPdfjsError: null,
        errorCode: null,
        activeRectangleId: null,
        documentNotRedactableDialogIsOpen: false,
        redactStorageWarningDialogIsOpen: false,
        isOcrConversionPending: false,
        ocrConversionProgress: 15,
        isReloadingFileAfterOcr: false,
      }
    },
    computed: {
      ...mapGetters('user', ['currentUserId']),
      ...mapState('files', ['fileError']),
      ...mapState('documentViewer', ['redactDocumentDialogIsOpen', 'redactDocumentError']),
      pageTitle () {
        if (!this.fileFromPdfjsError && !this.fileError) {
          return `${this.file.name || this.$t('common.DocumentViewer.loadingDocumentLabel')} - Closd`
        }
        return 'Closd'
      },
      shouldShowRedactAction() {
        return this.$vuetify.display.mdAndUp && !this.file.redactionBlockedReasons?.some(
          reason => IGNORED_REDACTION_BLOCKED_REASONS.includes(reason),
        )
      },
      filteredRedactionBlockedReasons() {
        return this.file.redactionBlockedReasons.filter(
          reason =>
            !IGNORED_REDACTION_BLOCKED_REASONS.includes(reason) &&
            // Do not show NO_PAGES reason for non-PDF document
            (reason !== 'NO_PAGES' || getFileExtension(this.file) === 'pdf'),
        )
      },
      showDocumentNotRedactableDialog() {
        return this.documentNotRedactableDialogIsOpen && this.filteredRedactionBlockedReasons.length > 0
      },
      allRectangles() {
        return [...this.drawnRectangles, ...this.searchedRectangles]
      },
      isRedactButtonDisabled() {
        return !this.allRectangles.length || this.isOcrConversionPending
      },
    },
    watch: {
      isRedactByZone (newValue) {
        if (newValue) {
          this.showRectangleDrawer = true
        }
      },
      isRedactByAI (newValue) {
        if (newValue) {
          this.showRectangleDrawer = true
        }
      },
      isRedactByKeywords (newValue) {
        if (newValue) {
          this.showRectangleDrawer = true
        }
      },
    },
    async created () {
      try {
        this.isLoading = true
        if (this.roomType === ROOM_ROUTE_TYPES.UNDEFINED) {
          try {
            // We call the service directly here to avoid interacting with the store and potentially
            // Recreating the current component in case the requests errors
            const { data: room } = await roomService.getRoomByMnemo(this.mnemo)
            if (room?.id) {
              this.$router.replace({
                name: ROOM_DOCUMENT_VIEWER_ROUTE_NAME,
                params: {
                  mnemo: this.mnemo,
                  fileId: this.fileId,
                  roomType: getRoomRouteType(room),
                },
              })
            }
          } catch (e) {
            // Proceed but log the error
            console.error('Error fetching current room for document viewer:', e)
          }
        }

        this.filePromise = this.GET_FILE({
          id: this.fileId,
          mnemo: this.mnemo,
        })
        this.file = await this.filePromise
      } catch (e) {
        this.errorCode = e.response.data?.errorCode || null
        console.error(e)
        toast.error(this.$t('common.DocumentViewer.getDocumentError'))
      } finally {
        this.isLoading = false
      }

      window.addEventListener('beforeunload', this.onBeforeUnload)
    },
    beforeUnmount() {
      window.removeEventListener('beforeunload', this.onBeforeUnload)
      sessionStorage.removeItem('redactWarningWasSkipped')
      this.stopListeningToOcrProgress()
    },
    methods: {
      ...mapActions('files', [FilesActionTypes.GET_FILE]),
      ...mapMutations('documentViewer', [DocumentViewerMutationTypes.SET_REDACT_DOCUMENT_DIALOG]),
      async initializeViewer () {
        this.viewerInitialized = true
        // Make sure we have the file informations before doing anything else
        await this.filePromise

        if (this.$vuetify.display.mdAndUp && this.$route.query.isRedactMode === 'true') {
          // If the file wasn't redactable, we instantly force the URL to be the normal view
          // If it was redactable however, we'll wait for the viewer to be loaded before showing the redact pane
          if (!this.file.isRedactable) {
            this.$router.replace({
              name: ROOM_DOCUMENT_VIEWER_ROUTE_NAME,
              params: {
                mnemo: this.mnemo,
                fileId: this.fileId,
              },
            })
          }
        }
      },
      async onViewerPagesLoaded () {
        this.viewerLoaded = true

        if (this.isReloadingFileAfterOcr) {
          this.isReloadingFileAfterOcr = false
          return
        }

        if (this.$route.query.isRedactMode === 'true') {
          await this.filePromise
          if (this.file.isRedactable) {
            this.toggleRedactPane()
          }
        }
      },
      onDeleteRectangle (rectangle) {
        const index = this.drawnRectangles.indexOf(rectangle)
        if (index > -1) {
          this.drawnRectangles.splice(index, 1)
          this.$refs.rectangleDrawer.drawAllRectangles()

          toast.success(this.$t('common.PdfRedactingSidebarItem.deleteRectangleSuccess'))
        }
      },
      onUpdateSearchedRectangles(rectangles) {
        console.log('[DocumentViewer] Received updateRectangles event:', rectangles?.length || 0, 'rectangles')
        console.log('[DocumentViewer] Sample rectangles:', rectangles?.slice(0, 3))
        this.searchedRectangles = rectangles
        this.$nextTick(() => {
          if (this.$refs.rectangleDrawer) {
            console.log('[DocumentViewer] Triggering drawAllRectangles after searchedRectangles update')
            this.$refs.rectangleDrawer.drawAllRectangles()
          }
        })
      },
      onDeleteRectangleOnAllPages (rectangle) {
        for (let i = this.drawnRectangles.length - 1; i >= 0; i--) {
          if (this.drawnRectangles[i].index === rectangle.index) {
            this.drawnRectangles.splice(i, 1)
          }
        }
        this.$refs.rectangleDrawer.drawAllRectangles()

        toast.success(this.$t('common.PdfRedactingSidebar.deleteOnAllPagesSuccess'))
      },
      scrollAndFocusOnRectangle (id, options = {}) {
        this.activeRectangleId = id

        this.$nextTick(async () => {
          const rect = this.drawnRectangles.find(r => r.id === id)
          if (!rect) return

          if (options.from === 'pdfViewer') {
            // Scroll in sidebar
            const sidebarEl = this.$refs.redactSidebar?.$refs.sidebarScroll
            if (!sidebarEl) return

            const itemEl = sidebarEl.querySelector(`[data-rectangle-id="${rect.id}"]`)
            if (itemEl) {
              itemEl.scrollIntoView({ behavior: 'smooth', block: 'center' })
            }
          } else {
            // Scroll in PDF viewer
            const pdfViewer = this.$refs.documentPdfViewer.getPdfAppInstance().pdfViewer
            pdfViewer.scrollPageIntoView({
              pageNumber: rect.page,
            })

            // Wait until the page and its canvas are available
            const MAX_WAIT_TIME_MS = 2000
            const POLL_INTERVAL_MS = 100
            const MAX_RETRIES = MAX_WAIT_TIME_MS / POLL_INTERVAL_MS

            const viewerContainer = pdfViewer.container
            const waitForCanvas = async (pageNumber, retries = MAX_RETRIES, delay = POLL_INTERVAL_MS) => {
              for (let i = 0; i < retries; i++) {
                const pageEl = viewerContainer.querySelector(`.page[data-page-number="${pageNumber}"]`)
                const canvas = pageEl?.querySelector('.annotation-canvas')
                if (canvas) return canvas
                await new Promise(resolve => setTimeout(resolve, delay))
              }
              return null
            }
            const canvas = await waitForCanvas(rect.page)
            if (!canvas) {
              console.warn(`Canvas not found for page ${rect.page} after waiting`)
              return
            }

            // Calculate the position of the rectangle on the canvas
            const pageElement = canvas.closest('.page')
            const rectY = rect.y * canvas.clientHeight
            const rectHeight = rect.height * canvas.clientHeight

            // Center the view on the rectangle
            const pageTop = pageElement.offsetTop
            const scrollTarget = pageTop + rectY - viewerContainer.clientHeight / 2 + rectHeight / 2

            viewerContainer.scrollTo({
              top: scrollTarget,
              behavior: 'smooth',
            })
          }
        })
      },
      onDuplicateRectangle(rectangle) {
        const pdfViewer = this.$refs.documentPdfViewer.getPdfAppInstance()?.pdfViewer
        if (!pdfViewer) return

        const pageCount = pdfViewer.pagesCount
        for (let page = 1; page <= pageCount; page++) {
          if (page === rectangle.page) continue

          const rect = this.$refs.rectangleDrawer.createRectangle({
            page,
            x: rectangle.x,
            y: rectangle.y,
            width: rectangle.width,
            height: rectangle.height,
            index: rectangle.index, // Keep same index for copies
            isCopy: true,
          })
          this.drawnRectangles.push(rect)
        }

        this.$refs.rectangleDrawer.drawAllRectangles()

        toast.success(this.$t('common.PdfRedactingSidebar.duplicateOnAllPagesSuccess', { index: rectangle.index }))
      },
      confirmRedactDocument () {
        this.drawnRectangles = []
        this.searchedRectangles = []
        this.toggleRedactPane()
        this.file.pdfHasText = false

        // After redacting the document, we need to reload the document to see the changes
        this.$refs.documentPdfViewer.loadFile()

        const sidebarContentKeywordsComponent = this.$refs.redactSidebar?.$refs.redactSidebarContentKeywords
        if (sidebarContentKeywordsComponent) {
          sidebarContentKeywordsComponent.resetData()
        }

        // Send message to the BroadcastChannel
        // to notify Documents page that the document has been redacted
        const channel = new BroadcastChannel(BROADCAST.CHANNELS.DOCUMENT_REDACTION)
        channel.postMessage({ type: BROADCAST.MESSAGES.DOCUMENT_REDACTION_UPDATED })
        channel.close()
      },
      quitWithoutSaving () {
        this.drawnRectangles = []
        this.searchedRectangles = []
        // If the user tried to close the sidebar while there are redacted zones
        if (this.preventSidebarGoBack) {
          this.preventSidebarGoBack = false
          this.showRectangleDrawer = false
          this.$refs.redactSidebar.goBack()
        } else {
          this.toggleRedactPane()
          track('redact_cancelled')
        }
        this.$refs.rectangleDrawer.drawAllRectangles()
      },
      onRedactStorageWarningConfirm(hideWarningChecked) {
        this.redactStorageWarningDialogIsOpen = false
        if (hideWarningChecked) {
          setUserSetting(this.currentUserId, REDACT_WARNING_KEY, true)
        }
        this.toggleRedactPane(true)
      },
      toggleRedactPane(skipStorageWarningDialog) {
        if (this.file.isRedactable === false) {
          this.documentNotRedactableDialogIsOpen = true
          return
        }

        if (!this.redactPaneOpen && !skipStorageWarningDialog) {
          const hideWarning = getUserSetting(this.currentUserId, REDACT_WARNING_KEY)
            || this.$route.query.skipRedactWarning === 'true'
            || sessionStorage.getItem('redactWarningWasSkipped') === 'true'

          if (!hideWarning) {
            this.redactStorageWarningDialogIsOpen = true
            sessionStorage.setItem('redactWarningWasSkipped', 'true')
            return
          }
        }

        if (this.allRectangles.length > 0) {
          this.preventRedactPaneClose = true
          this.SET_REDACT_DOCUMENT_DIALOG(true)
          return
        }

        const outerContainer = this.$refs.documentPdfViewer.getPdfAppInstance().appConfig.sidebar.outerContainer
        if (this.redactPaneOpen) {
          outerContainer.classList.remove('rightSidebarOpen')
        } else {
          outerContainer.classList.add('rightSidebarOpen')
        }

        this.redactPaneOpen = !this.redactPaneOpen
        this.showRectangleDrawer = false
      },
      handleloadFileFromPdfjsError (error) {
        this.fileFromPdfjsError = error
        this.errorCode = error?.name
      },
      onRedactPaneFullyToggled () {
        this.$refs.documentPdfViewer.onViewportChanged()
      },
      closeRedactDocumentDialog () {
        this.preventRedactPaneClose = false
        this.SET_REDACT_DOCUMENT_DIALOG(false)
      },
      onBeforeUnload(event) {
        if (this.allRectangles.length > 0) {
          event.preventDefault()
        }
      },
      async launchOcrConversion() {
        this.isOcrConversionPending = true
        try {
          documentsService.postFileOcr(this.mnemo, this.fileId)
          this.startListeningToOcrProgress()
        } catch (error) {
          this.isOcrConversionPending = false
          console.error(error)
        }
      },
      startListeningToOcrProgress() {
        realtimeService.on([OcrNotifications.OCR_PROGRESS, OcrNotifications.OCR_FINISHED], this.handleOcrProgress)
      },
      stopListeningToOcrProgress() {
        realtimeService.off([OcrNotifications.OCR_PROGRESS, OcrNotifications.OCR_FINISHED], this.handleOcrProgress)
      },
      async handleOcrProgress (event, data) {
        if (data.file != this.fileId) {
          return
        }

        if (event === OcrNotifications.OCR_FINISHED) {
          this.stopListeningToOcrProgress()
          this.file.pdfHasText = true
          this.isOcrConversionPending = false
          this.isReloadingFileAfterOcr = true
          await this.$refs.documentPdfViewer.loadFile()
          this.$refs.rectangleDrawer.updateCanvasForVisiblePages(true)
          toast.success(this.$t('common.DocumentViewer.ocrProcessFinished'))
        } else if (event === OcrNotifications.OCR_PROGRESS) {
          this.ocrConversionProgress = data.progress
        }
      },
    },
  }
</script>

<style>
:root {
  --right-sidebar-width: 480px;
  --right-sidebar-top-offset: 113px;
}

#outerContainer {
  box-sizing: content-box;
}

.toolbar-expanded {
  width: calc(100% - var(--right-sidebar-width));
  transition: width 0.3s ease;
}

.right-sidebar-container {
  width: var(--right-sidebar-width);
  margin-top: var(--right-sidebar-top-offset);
  height: calc(100vh - var(--right-sidebar-top-offset)) !important;
}

#outerContainer.rightSidebarOpen #viewerContainer:not(.pdfPresentationMode) {
  inset-inline-end: var(--right-sidebar-width);
  transition-property: inset-inline-start, inset-inline-end;
}

#outerContainer.sidebarOpen #viewerContainer:not(.pdfPresentationMode) {
  transition-property: inset-inline-start, inset-inline-end;
}
</style>
